<template>
  <div class="modal-overflow edit-task">
    <div class="row">
      <div class="col-md-5">
        <label>Task Name</label>
        <div class="task-text-container grey-font">
          <span class="invisible">{{ task.id }}</span>
          <h6>{{ getTaskNameWithOptionalRepeatCount(task) }}</h6>
        </div>
      </div>
      <div class="col-md-1">
        <label>&nbsp</label>
        <img @click.stop="viewFiles" v-if="canReadFiles" src="static/img/icon-folder.png" title="Task Files" class="files-icon"/>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label>Task Owner</label>
          <el-autocomplete
            style="width:100%"
            data-vv-as="owner"
            size="large"
            name="ownerId"
            class="inline-input"
            clearable
            :disabled="!canEdit"
            v-validate="modelValidations.ownerId"
            v-model="selectedOwner"
            :fetch-suggestions="findOwners"
            :trigger-on-focus="false"
            value-key="name"
            placeholder="Task Owner"
            @blur="verifyOwnerAutocompleteSelected"
            @select="setOwner">
          </el-autocomplete>
          <small class="text-danger" v-show="errors.has('ownerId')">
            {{ getError('ownerId') }}
          </small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label>Task Assignee</label>
          <el-autocomplete
            style="width:100%"
            data-vv-as="assignee"
            size="large"
            name="assigneeId"
            class="inline-input"
            clearable
            :disabled="!canEditAssignee"
            v-validate="assigneeValidationRules"
            v-model="selectedAssignee"
            :fetch-suggestions="findAssignees"
            :trigger-on-focus="false"
            value-key="name"
            placeholder="Task Assignee"
            @blur="verifyAssigneeAutocompleteSelected"
            @select="setAssignee">
          </el-autocomplete>
          <small class="text-danger" v-show="errors.has('assigneeId')">
            {{ getError('assigneeId') }}
          </small>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <label>Priority</label>
          <el-select name="priority"
                     :disabled="!canEdit"
                     class="select"
                     size="large"
                     data-vv-as="priority"
                     placeholder="Task Priority"
                     v-validate="modelValidations.priority"
                     v-model="edit.priority">
            <el-option v-for="option in priorities"
                       class="select"
                       :value="option.value"
                       :label="option.label"
                       :key="option.label">
            </el-option>
          </el-select>
          <small class="text-danger" v-show="errors.has('priority')">
            {{ getError('priority') }}
          </small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label>
            Status
            <span v-if="edit.completeLock" class="complete-lock">Complete Lock</span>
            <button v-if="isSuperAdmin" @click="toggleCompleteLock" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> {{ completeLockButtonLabel }} </button>
          </label>
          <el-select name="status"
                     class="select"
                     size="large"
                     data-vv-as="status"
                     placeholder="Task Status"
                     :disabled="!canEditStatus"
                     v-model="edit.status">
            <el-option v-for="option in statuses"
                       class="select"
                       :value="option.value"
                       :label="option.label"
                       :key="option.label">
            </el-option>
          </el-select>
          <small class="text-danger" v-show="errors.has('status')">
            {{ getError('status') }}
          </small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label>Due Date</label>
          <date-picker
            name="dueDate"
            :disabled="!canEdit"
            data-vv-as="due date"
            v-model="edit.dueDate"
            v-validate="modelValidations.dueDate"
            type="date"
            @input="warnIfDueDateIsDayOff"
            placeholder="Pick a day"
            :format="constants.DATE.FORMAT.DISPLAY"
            :value-format="constants.DATE.FORMAT.VALUE"
            :editable=true
            :clearable=false
            :picker-options="datePickerConfig">
          </date-picker>
          <small class="text-danger" v-show="errors.has('dueDate')">
            {{ getError('dueDate') }}
          </small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group" v-if="isInvoiceByTask || (isInvoiceByMultipleTasks && !edit.name.startsWith('Invoice'))">
          <span class="invisible">Linked Task Id: {{ edit.linkedTaskId }}</span>
          <label>Linked Task<span v-if="edit.linkedTaskId"> - <a href="#" @click="openLinkedTask(edit.linkedTaskId)">View</a></span></label>
          <el-select name="linkedTaskId"
                     class="select"
                     size="large"
                     clearable
                     data-vv-as="status"
                     placeholder="Linked Task"
                     v-validate="modelValidations.linkedTaskId"
                     :disabled="!canEdit || !canEditLinkedTask"
                     v-model="edit.linkedTaskId">
            <el-option v-for="option in tasksInJob"
                       class="select"
                       :value="option.value"
                       :label="option.label"
                       :key="option.value">
            </el-option>
          </el-select>
        </div>
        <div v-else-if="isInvoiceByJob">
          <label>Invoice by Job</label>
          <div class="task-text-container grey-font" v-if="job && job.id">
            <span class="invisible">{{ job.id }}</span>
            <h6><a href="#" @click.prevent="viewJob">{{ job.name }}</a></h6>
          </div>
        </div>
        <div v-else-if="isInvoiceTask">
          <h6 style="margin-top: 3px;">Linked Tasks</h6>
          <el-table class="table-striped wrap-whole-words"
                    :data="linkedTasks"
                    border
                    :show-header="false"
                    style="width: 100%">
            <el-table-column key="Linked Task" prop="label">
              <template v-slot="props">
                <a href="#" @click="openLinkedTask(props.row.value)">{{ props.row.label }}</a>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <div class="form-group" style="width: 99%">
          <label>Notes</label>
          <ckeditor :disabled="!canEdit" :editor="editor" v-model="edit.notes" :config="ckEditorConfig"></ckeditor>
        </div>
        <span v-if="projectName" class="m-right-12">
          Project: <a @click.prevent="closeModalAndGoToProject" href="#">{{projectName}}</a>
        </span>
        <span v-if="job">
          Job: <a @click.prevent="viewJob" href="#">{{job.name}}</a>
        </span>
      </div>
      <div class="col-md-3">
        <label>Client & Builder Notes</label>
        <div v-if="client">Client: <a href="#" @click="openCompanyNotes(client)"><strong>{{client.name}}</strong></a></div>
        <div v-if="builder">Builder: <a href="#" @click="openCompanyNotes(builder)"><strong>{{builder.name}}</strong></a></div>
      </div>
      <div class="col-md-3" v-if="edit.completionDate">
        <label>Actual Completion Date</label>
        <date-picker
          name="completionDate"
          style="width: 99%"
          :disabled="true"
          v-model="edit.completionDate"
          type="date"
          :format="constants.DATE.FORMAT.DISPLAY"
          :value-format="constants.DATE.FORMAT.VALUE"
          :editable=false
          :picker-options="datePickerConfig">
        </date-picker>
      </div>
    </div>
    <div class="row" style="margin-top: 15px;">
      <div class="col-md-5">
        <h6 class="fields-header">User Fields</h6>
        <div v-for="field in activeTaskTemplate.fields">
          <component
            v-if="edit.fieldValues && edit.fieldValues[field.key] !== undefined && !field.system"
            :is="field.component"
            :disabled="!canEdit"
            :field="field"
            :task="task"
            :value="edit.fieldValues[field.key]"
            :commit="'tasks/edit/editFieldValue'">
          </component>
        </div>
      </div>
      <div class="col-md-3">
        <h6 class="fields-header">System Fields</h6>
        <div v-for="field in activeTaskTemplate.fields">
          <component
            v-if="edit.fieldValues && edit.fieldValues[field.key] !== undefined && field.system"
            :is="field.component"
            :disabled="!canEditSystemTaskValue"
            :field="field"
            :task="task"
            :value="edit.fieldValues[field.key]"
            :commit="'tasks/edit/editFieldValue'">
          </component>
        </div>
      </div>
      <div class="col-md-4">
        <auditable-entity :data="task"></auditable-entity>
      </div>
    </div>
    <div class="row" style="margin-top: 15px" v-if="canEdit">
      <div class="col-md-12">
        <button type="submit" :disabled="isSubmitDisabled" @click.prevent="validateAndSubmit" class="btn btn-fill btn-info btn-wd pull-right">Save</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  warnIfDueDateIsDayOff,
  fileSystemPathForTask,
  getRepeatCount,
  notifyError,
  notifySuccess,
  notifyWarning,
  taskNameWithOptionalRepeatCountSuffix,
  verifyAutocompleteSelected
} from 'src/utilities/utils'
import {mapFields} from 'vee-validate'
import {mapGetters} from 'vuex'
import {Autocomplete, DatePicker} from 'element-ui'
import Constants from 'src/utilities/constants'
import Permissions from 'src/utilities/permissions'
import Input from 'src/components/UIComponents/Wrappers/InputWrapper'
import Select from 'src/components/UIComponents/Wrappers/SelectWrapper'
import SelectMultiple from 'src/components/UIComponents/Wrappers/SelectMultipleWrapper'
import PriceRule from 'src/components/UIComponents/Wrappers/PriceRuleWrapper'
import InvoiceableTaskPriceRule from '@/components/UIComponents/Wrappers/InvoiceableTaskPriceRuleWrapper'
import HTML from 'src/components/UIComponents/Wrappers/CKEditorWrapper'
import Switch from 'src/components/UIComponents/Wrappers/SwitchWrapper'
import Tags from 'src/components/UIComponents/Wrappers/TagsWrapper'
import Checkbox from 'src/components/UIComponents/Wrappers/CheckboxWrapper'
import Radio from 'src/components/UIComponents/Wrappers/RadioWrapper'
import Date from 'src/components/UIComponents/Wrappers/DateWrapper'
import Currency from 'src/components/UIComponents/Wrappers/CurrencyWrapper'
import Numeric from 'src/components/UIComponents/Wrappers/NumericWrapper'
import TimeTracking from 'src/components/UIComponents/Wrappers/TimeTrackingWrapper'
import TimeTrackingSelect from 'src/components/UIComponents/Wrappers/TimeTrackingSelectWrapper'
import TimePicker from 'src/components/UIComponents/Wrappers/TimePickerWrapper'
import TimeSelect from 'src/components/UIComponents/Wrappers/TimeSelectWrapper'
import datePickerConfig from 'src/components/UIComponents/DatePicker/datePickerConfig'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import ckEditorConfig from 'src/utilities/ckeditor'
import AuditableEntity from 'src/components/UIComponents/AuditableEntity'
import {OK} from 'http-status-codes'
import {sync} from 'vuex-pathify'
import _ from 'lodash'

export default {
  props: ['data'],
  components: {
    AuditableEntity,
    DatePicker, // local due date date picker
    [Autocomplete.name]: Autocomplete,
    Input,
    Select,
    SelectMultiple,
    PriceRule,
    InvoiceableTaskPriceRule,
    HTML,
    Switch,
    Tags,
    Checkbox,
    Radio,
    Currency,
    Numeric,
    TimeTracking,
    TimeTrackingSelect,
    TimeSelect,
    TimePicker,
    ckeditor: CKEditor.component,
    Date // dynamic date picker wrapped in DateWrapper
  },
  computed: {
    edit: sync('tasks/edit'),
    ...mapFields([
      'nameId',
      'templateId',
      'ownerId',
      'assigneeId',
      'priority',
      'status',
      'dueDate',
      'notes'
    ]),
    statuses() {
      const statuses = this.$store.getters['statuses/ptracTask']
      const completeLock = this.$store.getters['tasks/edit/completeLock']

      // if completeLock is enabled OR
      // if the current task is not assigned to the current user AND the current user doesn't have permission to complete tasks for other users
      if (completeLock || (this.userId !== this.task.assigneeId
        && this.$can('edit', Permissions.COMPLETE_TASKS.NOT_ASSIGNED_TO_USER) === false)) {
        // return all statuses except 'Completed' && 'Cancelled'
        return _.filter(statuses, (statusKvp) => statusKvp.value !== Constants.STATUSES.COMPLETED && statusKvp.value !== Constants.STATUSES.CANCELLED)
      }

      return statuses
    },
    ...mapGetters({
      task: 'tasks/edit/getTask',
      userId: 'security/userId',
      users: 'users/kvpsForSelect',
      usersForObjectById: 'users/results',
      priorities: 'priorities/ptracTask',
      taskNames: 'tasks/names/kvpsForSelect',
      activeTaskTemplate: 'tasks/templates/active'
    }),
    assigneeValidationRules() {
      if (this.edit.status === Constants.STATUSES.COMPLETED && !this.selectedAssignee) {
        return { required: true };
      }
      return '';
    },
    linkedTasks() {
      const linkedTaskIds = this.task.fieldValues.linkedTaskIds || []
      return this.tasksInJob.filter((taskOption) => linkedTaskIds.includes(taskOption.value))
    },
    isInvoiceTask() {
      return this.task.name === Constants.TASKS.NAMES.INVOICE
    },
    isInvoiceByMultipleTasks() {
      return this.job && this.job.invoiceBy === Constants.INVOICE_BY.MULTIPLE_TASKS
    },
    isInvoiceByTask() {
      return this.job && this.job.invoiceBy === Constants.INVOICE_BY.TASK
    },
    isInvoiceByJob() {
      return this.job && this.job.invoiceBy === Constants.INVOICE_BY.JOB
    },
    isSuperAdmin() {
      return this.$store.get('security/isInGroup', Constants.GROUPS.SUPER_USERS)
    },
    canRead() {
      return this.$can('read', Permissions.TASKS)
    },
    canReadFiles() {
      return this.$can('read', Permissions.FILES)
    },
    canEdit() {
      return this.$can('edit', Permissions.TASKS) || this.userId === this.task.assigneeId
    },
    canEditStatus() {
      if (this.task.completionDate || this.task.completeLock) {
        return false
      }
      return this.$can('edit', Permissions.TASKS) || this.userId === this.task.assigneeId
    },
    canEditAssignee() {
      return this.$can('edit', Permissions.ASSIGN_TASKS)
    },
    canEditLinkedTask() {
      return this.$can('edit', Permissions.LINKED_TASKS)
    },
    canEditSystemTaskValue() {
      return this.$can('edit', Permissions.SYSTEM_TASK_VALUES)
    },
    isSubmitDisabled() {
      return (!this.client
        && _.get(this.task, 'name')
        && this.task.name.startsWith(Constants.TASKS.NAMES.INVOICE_SUBMITTAL))
        || this.isProcessing
    },
    completeLockButtonLabel() {
      return this.task.completeLock ? 'Unlock' : 'Lock'
    }
  },
  data() {
    return {
      editor: ClassicEditor,
      ckEditorConfig,
      datePickerConfig,
      constants: Constants,
      selectedAssignee: null,
      selectedOwner: null,
      job: null,
      client: null,
      builder: null,
      statusOnLoad: null,
      linkedTask: null,
      tasksInJob: [],
      projectName: '',
      isProcessing: false,
      modelValidations: {
        ownerId: {
          required: true
        },
        priority: {
          required: true
        },
        status: {
          required: true
        },
        dueDate: {
          required: false
        },
        notes: {
          required: false
        }
      }
    }
  },
  methods: {
    toggleCompleteLock() {
      this.$store.commit('tasks/edit/completeLock', !this.task.completeLock)
    },
    openCompanyNotes(company) {
      this.$eventHub.$emit('show-modal-4', 'Company Notes', { company, departmentId: this.job.departmentId }, '600px')
    },
    async openLinkedTask(taskId) {
      await this.fetchAndInit(taskId)
      window.history.back()
    },
    getError(fieldName) {
      return this.errors.first(fieldName)
    },
    getTaskNameWithOptionalRepeatCount(task) {
      return taskNameWithOptionalRepeatCountSuffix(task)
    },
    findAssignees(text, cb) {
      this.$store.commit('tasks/edit/assigneeId', null)
      cb(this.$store.getters['users/findByNameStartsWithExcludeInactive'](text))
    },
    setAssignee(assignee) {
      if (assignee) {
        this.selectedAssignee = assignee.name
        this.$store.commit('tasks/edit/assigneeId', assignee.id)
        warnIfDueDateIsDayOff(this, this.task)
      }
    },
    warnIfDueDateIsDayOff() {
      warnIfDueDateIsDayOff(this, this.task)
    },
    verifyAssigneeAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'tasks/edit@assigneeId', 'assignee', this.selectedAssignee)
    },
    findOwners(text, cb) {
      this.$store.commit('tasks/edit/ownerId', null)
      cb(this.$store.getters['users/findByNameStartsWithExcludeInactive'](text))
    },
    setOwner(owner) {
      if (owner) {
        this.selectedOwner = owner.name
        this.$store.commit('tasks/edit/ownerId', owner.id)
      }
    },
    verifyOwnerAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'tasks/edit@ownerId', 'owner', this.selectedOwner)
    },
    async validateAndSubmit() {
      this.isProcessing = true

      if (await this.$validator.validateAll()) {
        const updatedTask = JSON.parse(JSON.stringify(this.$store.getters['tasks/edit/getTask']))

        const payload = {
          id: this.$store.getters['tasks/edit/id'],
          endpoint: 'tasks',
          data: updatedTask
        }

        const response = await this.$store.dispatch('save2', payload)

        if (OK === response.status) {
          updatedTask.id = this.$store.getters['tasks/edit/id']
          this.$store.commit('tasks/edit/reset')
          if (updatedTask.status === Constants.STATUSES.COMPLETED
            || updatedTask.status === Constants.STATUSES.CANCELLED) {
            this.$eventHub.$emit('taskCompletedOrCancelled')
          }
          this.$eventHub.$emit('taskSaved', response.data)
          notifySuccess(this, 'Task Updated Successfully')
          this.$emit('close')
          return
        }

        notifyError(this, 'Error Updating Task', response.errors)
      }
      this.isProcessing = false
    },
    maybeLoadAssignee() {
      const assigneeId = this.$store.getters['tasks/edit/assigneeId']
      if (assigneeId) {
        const assignee = this.$store.getters['users/byId'](assigneeId)
        if (_.get(assignee, 'name')) {
          this.selectedAssignee = assignee.name
          return
        }
      }

      this.selectedAssignee = null
    },
    maybeLoadOwner() {
      const ownerId = this.$store.getters['tasks/edit/ownerId']
      if (ownerId) {
        const owner = this.$store.getters['users/byId'](ownerId)
        if (_.get(owner, 'name')) {
          this.selectedOwner = owner.name
        }
      }
    },
    handleEditJob(jobId) {
      notifyWarning(this, 'Please Select a Billable Client')
      this.$eventHub.$emit('show-modal-3', 'Edit Job', {id: jobId}, '1000px')
    },
    async maybePoRequiredWarn() {
      if (this.job.clientId) {
        const clientResponse = await this.$store.dispatch('fetchById', {
          endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
          id: this.job.clientId
        })
        if (OK === clientResponse.status && clientResponse.data.poRequired) {
          notifyWarning(this, `The Client ${clientResponse.data.name} requires a Purchase Order. Please add a Purchase Order Number to Invoice`)
        }
      }
    },
    async fetchTask(taskId) {
      const response = await this.$store.dispatch('tasks/edit/fetchById', taskId)

      if (this.$store.getters['tasks/edit/linkedTaskId']) {
        const linkedTaskResponse = await this.$store.dispatch('fetchById', {
          endpoint: 'tasks',
          id: this.$store.getters['tasks/edit/linkedTaskId']
        })

        if (OK === linkedTaskResponse.status) {
          this.linkedTask = linkedTaskResponse.data
        }
      }
      return response
    },
    async init(taskId) {
      const responseJob = await this.$store.dispatch('fetchById', {endpoint: 'jobs', id: this.$store.getters['tasks/edit/jobId']})

      if (OK === responseJob.status) {
        this.job = responseJob.data

        if (this.job.clientId) {
          const responseClient = await this.$store.dispatch('fetchById', {endpoint: 'companies', id: this.job.clientId})
          if (OK === responseClient.status) {
            this.client = responseClient.data
          }
        }
        if (this.job.builderId) {
          const responseBuilder = await this.$store.dispatch('fetchById', {endpoint: 'companies', id: this.job.builderId})
          if (OK === responseBuilder.status) {
            this.builder = responseBuilder.data
          }
        }

        const taskName = this.$store.getters['tasks/edit/name']
        if (!this.client && taskName.startsWith(Constants.TASKS.NAMES.INVOICE)) {
          this.handleEditJob(this.$store.getters['tasks/edit/jobId'])
        }

        if (this.task.name.startsWith(Constants.TASKS.NAMES.INVOICE) && !this.task.fieldValues.poNumber) {
          await this.maybePoRequiredWarn()
        }

        if (await this.$store.dispatch('tasks/fetchTasksByJobId', this.job.id)) {
          const otherTasks = this.$store.getters['tasks/tasksInJobForSelect']
          this.tasksInJob = _.filter(otherTasks, (task) => task.value !== taskId)
        }
      }

      if (this.task.status) {
        this.statusOnLoad = JSON.parse(JSON.stringify(this.task.status))
      }

      this.maybeLoadAssignee()
      this.maybeLoadOwner()
    },
    jobSaved() {
      this.fetchAndInit(null)
    },
    async fetchAndInit(taskId) {
      if (!taskId) {
        taskId = this.data.id
      }

      const response = await this.fetchTask(taskId)

      if (OK !== response.status) {
        notifyError(this, "Failed to load task by ID: " + taskId)
        return
      }

      await this.init(taskId)
    },
    async viewFiles() {
      const departmentResponse = await this.$store.dispatch('departments/fetchById', this.job.departmentId)
      if (OK !== departmentResponse.status) {
        notifyError(this, "Failed to load department by ID: " + this.job.departmentId)
        return
      }

      const projectResponse = await this.$store.dispatch('projects/fetchById', this.task.projectId)
      if (OK !== projectResponse.status) {
        notifyError(this, "Failed to load project by ID: " + this.task.projectId)
        return
      }

      const path = fileSystemPathForTask(
        projectResponse.data.name,
        this.task.projectId,
        departmentResponse.data.name,
        this.job.name,
        this.job.id,
        this.task.name,
        this.task.id,
        getRepeatCount(this.task)
      )

      this.$eventHub.$emit('show-modal-4', 'File Browser', path, '600px')
    },
    viewJob() {
      this.$eventHub.$emit('show-modal-3', 'View Job', { id: this.edit.jobId }, '1000px')
    },
    closeModalAndGoToProject() {
      this.$emit('close')
      if (this.$route.path !== `/projects/${this.edit.projectId}`) {
        this.$router.push({path: `/projects/${this.edit.projectId}`})
      }
    },
    async fetchProject() {
      const response = await this.$store.dispatch('fetchById', {endpoint: 'projects', id: this.edit.projectId})

      if (OK === response.status) {
        this.projectName = response.data.name
      }
    }
  },
  beforeCreate() {
    this.$store.commit('tasks/edit/reset') // make sure vuex is empty when loading a task Modal
    this.$store.cache.dispatch('statuses/fetchByEntityType', 'PtracTask')
    this.$store.cache.dispatch('priorities/fetchByEntityType', 'PtracTask')
    this.$store.cache.dispatch('tasks/names/fetchAll')
    this.$store.cache.dispatch('users/fetchAll')
  },
  async beforeMount() {
    this.$eventHub.$on('jobSaved', this.jobSaved)
    await this.fetchAndInit()
    await this.fetchProject()
  },
  beforeDestroy() {
    this.$eventHub.$off('jobSaved')
  },
  watch: {
    selectedAssignee: {
      handler: function (selectedAssignee) {
        if (!selectedAssignee) {
          this.$store.commit('tasks/edit/assigneeId', null)
        }
      }
    },
    selectedOwner: {
      handler: function (selectedOwner) {
        if (!selectedOwner) {
          this.$store.commit('tasks/edit/ownerId', null)
        }
      }
    }
  }
}
</script>

<style scoped>
.complete-lock {
  background-color: #cc2127;
  color: white;
  padding: 1px 5px 1px 5px;
  border-radius: 10px;
}
label {
  display: block;
}

.files-icon {
  cursor: pointer;
  margin-top: 8px;
}
.task-text-container {
  background-color: lightgrey;
  padding: 11px 0 4px 8px;
  border-radius: 5px;
}
.fields-header {
  padding-bottom: 5px;
  margin-bottom: 12px;
  border-bottom: 1px dashed #aaa;
}
.edit-task >>> .tiny-label-button {
  padding: 1px 5px;
  margin-top: 3px;
  float: right;
}
</style>
